/**
 * Circular Progress Component
 */

import { v4 as uuidv4 } from 'uuid'

import { IBuilderComponent } from '../../base'
import type {
  ColorAdaptiveValue,
  ColorVariant,
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
  VariableTextValue,
} from '../../types'
import {
  deepMerge,
  parseAlignmentFormat,
  parseColorFormat,
  parseResponsiveFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseTypographyFormat,
} from '../../parser-utils'
import {
  formatFromVariableText,
  formatFromVariableSelect,
  formatSpacingFormat,
  formatTypographyFormat,
  formatColorFormat,
  formatResponsiveValue,
  formatComponentAlignmentFormat,
} from '../../format-utils'
import circularProgressTemplate from './circular-progress.template.json'

const CIRCULAR_PROGRESS_DEFAULT_TEMPLATE = circularProgressTemplate as unknown as ICircularProgressComponentSettings

export interface ICircularProgressComponentSettings {
  // Data settings
  'circular-progress-data-value': VariableTextValue | { flag: number; id: string; value: string }
  'circular-progress-data-max-value': { flag: number; id: string; value: string } | any
  'circular-progress-data-heading': VariableTextValue

  // Design settings
  'circular-progress-design-active-indicator-color'?: any
  'circular-progress-design-scheme'?: any
  'circular-progress-design-track-color'?: any
  'circular-progress-design-show-value'?: any
  'circular-progress-design-typography'?: ResponsiveValue<TypographyValue>
  'circular-progress-design-heading-typography'?: TypographyValue
  'circular-progress-design-label-typography'?: TypographyValue
  'circular-progress-design-size'?: ResponsiveValue<string>
  'circular-progress-design-alignment'?: ResponsiveValue<any>
  'circular-progress-design-heading-alignment'?: ResponsiveValue<any>
  'circular-progress-design-margin'?: ResponsiveValue<SpacingInputValue>
  'circular-progress-design-heading-margin'?: ResponsiveValue<SpacingInputValue>
  'circular-progress-design-group-color'?: any
  'circular-progress-design-label-color'?: any
  'circular-progress-design-label-color-solid'?: any
  'circular-progress-design-label-color-start-gradient'?: any
  'circular-progress-design-label-color-end-gradient'?: any
  'circular-progress-design-label-color-gradient-direction'?: any
  'circular-progress-design-value-color'?: any
  'circular-progress-design-value-color-solid'?: ColorAdaptiveValue<ColorVariant>
  'circular-progress-design-value-color-start-gradient'?: ColorAdaptiveValue<ColorVariant>
  'circular-progress-design-value-color-end-gradient'?: ColorAdaptiveValue<ColorVariant>
  'circular-progress-design-value-color-gradient-direction'?: ColorAdaptiveValue<ColorVariant>
  'circular-progress-design-circle-color'?: any
  'circular-progress-design-circle-color-solid'?: any
  'circular-progress-design-circle-color-start-gradient'?: any
  'circular-progress-design-circle-color-end-gradient'?: any
  'circular-progress-design-circle-color-gradient-direction'?: any
}

export interface ICircularProgressComponent extends IBuilderComponent {
  componentId: 'circular-progress'
  groupId: 'progress'
  settings: ICircularProgressComponentSettings
}

export interface AiCircularProgressInput {
  value?: string | number
  maxValue?: string | number
  heading?: string[] // Format: ["text:Progress"] or ["text:Loading ", "cr:itemName"]
  size?: string
  alignment?: string
  headingAlignment?: string
  margin?: string
  headingMargin?: string
  typography?: string
  headingTypography?: string
  showValue?: boolean
  trackColor?: string
  valueColor?: string
  circleColor?: string
}

export class CircularProgressComp {
  getTemplate(): ICircularProgressComponentSettings {
    return CIRCULAR_PROGRESS_DEFAULT_TEMPLATE
  }

  /**
   * format circular progress component settings to AI circular progress input
   */
  format(settings: ICircularProgressComponentSettings): AiCircularProgressInput {
    const result: AiCircularProgressInput = {}

    // Data - Value (from VariableSelectValue or VariableTextValue)
    if (settings['circular-progress-data-value']) {
      if ('flag' in settings['circular-progress-data-value'] && 'value' in settings['circular-progress-data-value']) {
        const value = formatFromVariableSelect(settings['circular-progress-data-value'] as any)
        if (value && value.startsWith('text:')) {
          const numValue = parseFloat(value.substring(5))
          result.value = isNaN(numValue) ? value.substring(5) : numValue
        } else {
          result.value = value
        }
      } else {
        const value = formatFromVariableText(settings['circular-progress-data-value'] as any)
        if (value.length > 0) {
          const numValue = parseFloat(value.join(' '))
          result.value = isNaN(numValue) ? value.join(' ') : numValue
        }
      }
    }

    // Data - Max Value
    if (settings['circular-progress-data-max-value'] && 'value' in settings['circular-progress-data-max-value']) {
      const maxValue = (settings['circular-progress-data-max-value'] as any).value
      const numValue = parseFloat(String(maxValue))
      result.maxValue = isNaN(numValue) ? maxValue : numValue
    }

    // Data - Heading
    if (settings['circular-progress-data-heading']) {
      result.heading = formatFromVariableText(settings['circular-progress-data-heading'])
    }

    // Design - Size
    if (settings['circular-progress-design-size']) {
      const size = settings['circular-progress-design-size']
      const values = Object.values(size)
      if (values.length > 0) {
        const allSame = values.every((v) => v === values[0])
        if (allSame) {
          result.size = values[0]
        } else {
          result.size = formatResponsiveValue(size, (val) => String(val))
        }
      }
    }

    // Design - Alignment (array format)
    if (settings['circular-progress-design-alignment']) {
      // Convert array format back to simple alignment
      const alignmentValues = Object.fromEntries(
        Object.entries(settings['circular-progress-design-alignment']).map(([key, value]) => [
          key,
          Array.isArray(value) && value.length > 0 ? value[0] : value,
        ])
      ) as any
      result.alignment = formatComponentAlignmentFormat(alignmentValues)?.replace(/^h:/, '') || undefined
    }

    // Design - Heading Alignment (array format)
    if (settings['circular-progress-design-heading-alignment']) {
      const headingAlignmentValues = Object.fromEntries(
        Object.entries(settings['circular-progress-design-heading-alignment']).map(([key, value]) => [
          key,
          Array.isArray(value) && value.length > 0 ? value[0] : value,
        ])
      ) as any
      result.headingAlignment = formatComponentAlignmentFormat(headingAlignmentValues)?.replace(/^h:/, '') || undefined
    }

    // Design - Margin
    if (settings['circular-progress-design-margin']) {
      result.margin = formatSpacingFormat(settings['circular-progress-design-margin'])
    }

    // Design - Heading Margin
    if (settings['circular-progress-design-heading-margin']) {
      result.headingMargin = formatSpacingFormat(settings['circular-progress-design-heading-margin'])
    }

    // Design - Typography
    if (settings['circular-progress-design-typography']) {
      result.typography = formatTypographyFormat(settings['circular-progress-design-typography'])
    }

    // Design - Heading Typography (single TypographyValue)
    if (settings['circular-progress-design-heading-typography']) {
      result.headingTypography = formatTypographyFormat({ phone: settings['circular-progress-design-heading-typography'] } as any)
    }

    // Design - Show Value
    if (settings['circular-progress-design-show-value'] !== undefined) {
      result.showValue = settings['circular-progress-design-show-value']
    }

    // Design - Track Color (simple string)
    if (settings['circular-progress-design-track-color']) {
      result.trackColor = settings['circular-progress-design-track-color'] as string
    }

    // Design - Value Color
    if (settings['circular-progress-design-value-color']) {
      result.valueColor = formatColorFormat({
        colorType: settings['circular-progress-design-value-color'] as any,
        colorSolid: settings['circular-progress-design-value-color-solid'],
        colorStartGradient: settings['circular-progress-design-value-color-start-gradient'],
        colorEndGradient: settings['circular-progress-design-value-color-end-gradient'],
        colorGradientDirection: settings['circular-progress-design-value-color-gradient-direction'] as any,
      })
    }

    // Design - Circle Color (simple string)
    if (settings['circular-progress-design-circle-color']) {
      result.circleColor = settings['circular-progress-design-circle-color'] as string
    }

    return result
  }

  /**
   * parse AI circular progress input to circular progress component settings
   */
  parse(input: AiCircularProgressInput): ICircularProgressComponentSettings {
    const result = deepMerge(CIRCULAR_PROGRESS_DEFAULT_TEMPLATE, {})

    // Data - Value
    if (input.value !== undefined) {
      result['circular-progress-data-value'] = {
        flag: 2048, // CustomText flag
        id: uuidv4(),
        value: String(input.value),
      }
    }

    // Data - Max Value
    if (input.maxValue !== undefined) {
      result['circular-progress-data-max-value'] = {
        flag: 2048, // CustomText flag
        id: uuidv4(),
        value: String(input.maxValue),
      }
    }

    // Data - Heading
    if (input.heading !== undefined) {
      result['circular-progress-data-heading'] = parseToVariableText(input.heading)
    }

    // Design - Size
    if (input.size) {
      result['circular-progress-design-size'] = parseResponsiveFormat(input.size, (val) => val)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentParsed = parseAlignmentFormat(input.alignment)
      if (alignmentParsed.hAlign) {
        // Map ComponentAlignment to array format
        const alignmentArray = Object.fromEntries(
          Object.entries(alignmentParsed.hAlign).map(([key, value]) => [key, [value]])
        ) as ResponsiveValue<any>
        result['circular-progress-design-alignment'] = alignmentArray
      }
    }

    // Design - Heading Alignment
    if (input.headingAlignment) {
      const alignmentParsed = parseAlignmentFormat(input.headingAlignment)
      if (alignmentParsed.hAlign) {
        const alignmentArray = Object.fromEntries(
          Object.entries(alignmentParsed.hAlign).map(([key, value]) => [key, [value]])
        ) as ResponsiveValue<any>
        result['circular-progress-design-heading-alignment'] = alignmentArray
      }
    }

    // Design - Margin
    if (input.margin) {
      result['circular-progress-design-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Heading Margin
    if (input.headingMargin) {
      result['circular-progress-design-heading-margin'] = parseSpacingFormat(input.headingMargin)
    }

    // Design - Typography
    if (input.typography) {
      result['circular-progress-design-typography'] = parseTypographyFormat(input.typography)
    }

    // Design - Heading Typography
    if (input.headingTypography) {
      const typo = parseTypographyFormat(input.headingTypography)
      // Apply to heading typography (single TypographyValue, not ResponsiveValue)
      const firstBreakpoint = Object.values(typo)[0]
      if (firstBreakpoint) {
        result['circular-progress-design-heading-typography'] = firstBreakpoint
      }
    }

    // Design - Show Value
    if (input.showValue !== undefined) {
      result['circular-progress-design-show-value'] = input.showValue
    }

    // Design - Track Color
    if (input.trackColor) {
      // Track color is a simple string (color variant)
      result['circular-progress-design-track-color'] = input.trackColor
    }

    // Design - Value Color
    if (input.valueColor) {
      const colorParsed = parseColorFormat(input.valueColor)
      result['circular-progress-design-value-color'] = colorParsed.colorType
      result['circular-progress-design-value-color-solid'] = colorParsed.colorSolid
      result['circular-progress-design-value-color-start-gradient'] = colorParsed.colorStartGradient
      result['circular-progress-design-value-color-end-gradient'] = colorParsed.colorEndGradient
      result['circular-progress-design-value-color-gradient-direction'] = colorParsed.colorGradientDirection as any
    }

    // Design - Circle Color
    if (input.circleColor) {
      // Circle color is a simple string (color variant)
      result['circular-progress-design-circle-color'] = input.circleColor
    }

    return result
  }
}
